import Link from "next/link";
import { useRouter } from "next/router";
import { FormEvent, FunctionComponent, useEffect, useState } from "react";
import { useUser } from "../../hooks/useUser";
import { betaSignUp } from "../../lib/api/auth/betaTester";
import { signIn } from "../../lib/api/auth/signin";
import { signUp } from "../../lib/api/auth/signup";
import { User } from "../../lib/api/user";
import { getErrorMessage } from "../../lib/form";
import Button from "../lib/Button/Button";
import { Heading } from "../lib/Text/Heading";
import TextField from "../lib/TextField/TextField";
import { UnexpectedValueError } from "../../lib/unexpected-value-error";
import { PlainString } from "../../lib/types/json/crypto";

type AuthFormProps = {
  isSignup?: boolean;
  isBetaSignup?: boolean;
};

const AuthForm: FunctionComponent<AuthFormProps> = ({
  isSignup,
  isBetaSignup,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();
  const [successMsg, setSuccessMsg] = useState<string>("");
  const { user, loading, mutate } = useUser();
  const router = useRouter();

  useEffect(() => {
    if (user) {
      router.push("/admin");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isLoading || loading) return;
    setIsLoading(true);
    const elements = event.currentTarget.elements;
    const emailElement = "email" in elements && elements.email as HTMLInputElement;
    const passwordElement = "password" in elements && elements.password;
    const invitationCode = "invitationCode" in elements && elements.invitationCode;

    try {
      if (!(emailElement instanceof HTMLInputElement))
        throw new UnexpectedValueError(emailElement, "Expected email element to be a HTMLInputElement");
      if (!(passwordElement instanceof HTMLInputElement))
        throw new UnexpectedValueError(passwordElement, "Expected password element to be a HTMLInputElement");

      const password = passwordElement.value as PlainString;

      let res: { user: User | null } = { user: null };
      if (isSignup) {
        if (!(invitationCode instanceof HTMLInputElement))
          throw new UnexpectedValueError(invitationCode, "Expected invitation code element to be a HTMLInputElement");

        res = await signUp({
          email: emailElement.value,
          password: password,
          invitationCode: invitationCode.value,
        });
      } else if (isBetaSignup) {
        res = { user: null, ...await betaSignUp(emailElement.value) };
        setErrorMsg(undefined);
        setSuccessMsg(
          "Erfolgreich angemeldet. Du wirst sofort weitergeleitet!"
        );
        router.push("/forms/demo/oB2IwnXyeB");
      } else {
        res = await signIn({
          email: emailElement.value,
          password: password,
        });
      }
      if (res) {
        await mutate({ user: res.user });
      }
    } catch (error) {
      setErrorMsg(getErrorMessage(error) ?? "(null)");
      setSuccessMsg("");
      setIsLoading(false);
    }
  }

  let authText = "Anmelden";

  if (isSignup) authText = "Registrieren";
  else if (isBetaSignup) authText = "Beta-Registrierung";

  return (
    <div className="max-w-md w-full mx-auto shadow-md bg-white py-12 px-6 mt-4 rounded-xl sm:py-16 sm:px-12 sm:rounded-2xl sm:mt-16">
      <div className="flex justify-between mb-8">
        <div className="flex items-center">
          <img
            src="/logo.png"
            className="h-6 w-6 rounded-full mr-2"
          />
          <p>Onlinevertrag.app</p>
        </div>
        {isSignup || isBetaSignup ? null : (
          <Link href="/signup">
            <a className="text-right text-primary no-underline hover:underline">
              Kein Account? <br />
              Jetzt registrieren
            </a>
          </Link>
        )}
        {!isBetaSignup ? null : (
          <Link href="/signup">
            <a className="text-right text-primary no-underline hover:underline">
              Code erhalten? <br />
              Jetzt registrieren
            </a>
          </Link>
        )}
      </div>
      <Heading level={1} className="normal-case tracking-tight mb-8">
        {authText}
      </Heading>
      <form onSubmit={e => { handleSubmit(e); }}>
        {errorMsg && <p className="text-red-500 mb-4">{errorMsg}</p>}
        {!!successMsg && <p className="text-green-500 mb-4">{successMsg}</p>}
        <div className="mb-4 sm:mb-6">
          <TextField
            name="email"
            type="email"
            autoComplete="email"
            required
            label="Email Adresse"
            placeholder="hello@onlinevertrag.app"
            showLabel={true}
          />
        </div>
        {isBetaSignup ? null : (
          <div className="mb-4 sm:mb-6">
            <TextField
              name="password"
              type="password"
              autoComplete="password"
              required
              label="Passwort"
              placeholder="passwort123"
              showLabel={true}
            />
          </div>
        )}
        {isSignup ? (
          <div className="mb-4 sm:mb-6">
            <TextField
              name="invitationCode"
              type="text"
              required
              autoComplete="off"
              label="Einladungs-Code"
              placeholder="Deine Einladung"
              showLabel={true}
            />
          </div>
        ) : null}
        <Button
          variant="primary"
          type="submit"
          isLoading={isLoading || loading}
        >
          {authText}
        </Button>
      </form>
    </div>
  );
};

export default AuthForm;
